import { useContext } from 'react'
import { useEffectOnce } from 'hooks'
import { prop } from 'ramda'
import { useDispatch } from 'react-redux'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import { DashContext } from 'context/dash'
import { useDrag, useDrop } from 'react-dnd'
import {
  getDashVehicleSerialNumber,
  makeGetLocationDetail,
  makeGetCanComponentDrop,
  makeMeetsOnDragRequirements,
  makeGetUnassignedLocationDetail,
  makeGetLocationAccepts,
  getIsUnassignedComponent,
} from 'selectors/dash'
import { getCanEditSwitchAndGauge } from 'selectors/authorization'
import {
  onDashDrag,
  offDashDrag,
  onDragDrop,
  clearDragItem,
} from 'actions/dash'
import { DASH_VIEW } from 'utils/constants'

export const useDashContext = () => useContext(DashContext)

export const useSetDashContext = (props) => {
  const { setInitialState } = useDashContext()
  useEffectOnce(() => setInitialState(props))
}

export const usePanelPartDrag = ({ location }) => {
  const dispatch = useDispatch()

  let detail = useMakeSelector(makeGetLocationDetail, location)

  const serialNumber = useSelector(getDashVehicleSerialNumber)
  const isDraggable = useMakeSelector(makeMeetsOnDragRequirements, location)
  const unassignedDetail = useMakeSelector(
    makeGetUnassignedLocationDetail,
    location
  )
  const canEdit = useSelector(getCanEditSwitchAndGauge, {
    serialNumber,
  })
  const isUnassignedComponent = useSelector(getIsUnassignedComponent, {
    key: location,
  })

  // Check for unassigned components in pre-prod vehicles and if location type selected then allow user to drag the part
  if (isUnassignedComponent) {
    if (unassignedDetail) {
      detail = unassignedDetail
    }
  }

  const item = {
    location,
    type: detail,
  }

  const [, dragRef] = useDrag({
    type: detail, // type is a special key in DnD, no type, no work, can be single or multiple details by seperator
    item: () => {
      dispatch(onDashDrag(item))
      return item
    },
    canDrag: () => isDraggable,
    end: () => {
      dispatch(offDashDrag())
    },
  })

  return {
    dragRef,
    isDraggable: canEdit ? isDraggable : false,
  }
}

export const usePanelPartDrop = ({ location }) => {
  const dispatch = useDispatch()
  const serialNumber = useSelector(getDashVehicleSerialNumber)
  const accept = useMakeSelector(makeGetLocationAccepts, location)
  const isDropAllowed = useMakeSelector(makeGetCanComponentDrop, location)
  const canEdit = useSelector(getCanEditSwitchAndGauge, {
    serialNumber,
  })

  const [{ canDrop, isOver }, dropRef] = useDrop({
    accept, // accept is a special key in DnD, no accept, no work, can be single or multi details seperated
    drop: (item) => {
      const onDropArgs = {
        toLocation: location,
        fromLocation: prop('location', item),
      }
      dispatch(clearDragItem())
      dispatch(onDragDrop(onDropArgs))
      dispatch(setSelectedIdWithDrawer(DASH_VIEW))
    },
    canDrop: () => (canEdit ? isDropAllowed : false),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  })

  return {
    canDrop,
    isOver,
    dropRef,
  }
}
